import { motion } from "framer-motion";
import { Handshake, Search, FileCheck, Shield, ArrowRight } from "lucide-react";

const steps = [
  {
    icon: Handshake,
    title: "Engage with PROVENANCE",
    description: "We scope the engagement with you, understand the deal context, and establish secure channels for the target company to provide access.",
  },
  {
    icon: Search,
    title: "Expert led deep dive",
    description: "Our team of senior technologists conducts comprehensive analysis across 6 pillars, combining proprietary tooling with hands on expert review.",
  },
  {
    icon: Shield,
    title: "Validation & sign off",
    description: "Every finding is validated by our internal review team. We don't just flag issues — we assess business impact and remediation complexity.",
  },
  {
    icon: FileCheck,
    title: "Investment ready report",
    description: "Receive your Technology Reality Score™ with plain English findings, risk assessment, and actionable recommendations for your investment committee.",
  },
];

export const HowItWorks = () => {
  return (
    <section id="how-it-works" className="relative py-24 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20" />
      
      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cream font-medium text-sm uppercase tracking-wider">
            Our process
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4 mb-6">
            How we work with you
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Technology due diligence shouldn't be a black box. Our transparent, 
            expert led process gives you confidence in what you're acquiring.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative"
            >
              <div className="flex flex-col items-center text-center">
                {/* Step number with icon */}
                <div className="relative mb-6">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-cream/30 bg-card">
                    <step.icon className="h-8 w-8 text-cream" />
                  </div>
                  <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-cream text-xs font-bold text-background">
                    {index + 1}
                  </span>
                </div>

                <h3 className="font-display text-xl font-semibold mb-3">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {step.description}
                </p>

                {index < steps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute -right-4 top-10 h-5 w-5 text-cream" />
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Differentiator callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 rounded-2xl border border-cream/20 bg-cream/5 p-8"
        >
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <div className="flex-shrink-0">
              <div className="h-16 w-16 rounded-full bg-cream/10 flex items-center justify-center">
                <Shield className="h-8 w-8 text-cream" />
              </div>
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold mb-2">
                Technology-assisted, human-validated
              </h3>
              <p className="text-muted-foreground">
                While we leverage proprietary technology to accelerate analysis, every finding is 
                reviewed and validated by our expert team. You're not getting an automated report — 
                you're getting senior technologist insight with the rigour of systematic analysis.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
